/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/** @jsxImportSource @emotion/react */
import {
  addScaleToMoney,
  Button,
  Icon,
  Input,
  Snackbar,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useMount } from 'react-use';
import { ErrorResponse, FormErrorResponse } from 'api/fetchAPI';
import { ErrorPage, Markdown } from 'components';
import { useOrderId } from 'context';
import { useOrderDetails, usePoll } from 'hooks';
import {
  completeExternalRefund,
  getTravelRuleInfoStatus,
  TravelRuleInfoStatus,
} from 'pages/api';
import { OrderStatus } from 'types';
import { redirectBasedOnStatus } from 'utils';
import { initiateExternalRefund } from './api';

const contactSupport = () => {
  window.open('mailto:support@quickbit.com');
};

export const Refund = () => {
  const { palette, spacing } = useTheme();
  const { order, error } = useOrderDetails();
  const orderId = useOrderId();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [refundResponseError, setRefundResponseError] = useState<boolean>(true);
  const [travelRuleWindow, setTravelRuleWindow] = useState<Window | null>(null);

  const openTravelRuleForm = (previewId: string) => {
    const openedWindow = window.open(
      `${process.env.REACT_APP_WEB_APP_BASE_URL}/travel-rule-beneficiary-info/${previewId}?isPayRefund=true`
    );
    setTravelRuleWindow(openedWindow);
  };

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setAddress(e.target.value);
      setAddressError(null);
    },
    [setAddress]
  );

  const handlePaste = useCallback(async () => {
    const pastedText = await navigator.clipboard.readText();
    setAddress(pastedText);
    setAddressError(null);
  }, [setAddress]);

  const refundAmount =
    order?.order.manualRefund?.refundAmount + ' ' + order?.order.currency;

  useMount(() => {
    if (order)
      if (order.order.status !== OrderStatus.MANUAL_REFUND_INITIATED)
        redirectBasedOnStatus(order?.order.status, navigate);
  });

  const [
    { error: completeRefundError, loading: completeRefundLoading },
    completeSend,
  ] = useAsyncFn(
    async (previewId: string) => {
      await completeExternalRefund(orderId, previewId);
      navigate('/refunding');
    },
    [orderId]
  );

  const [
    {
      value: initiateRefundValue,
      loading: initiateRefundLoading,
      error: initiateRefundError,
    },
    refund,
  ] = useAsyncFn(async () => {
    if (!order) return;
    const response = await initiateExternalRefund(
      orderId,
      address,
      addScaleToMoney({
        amount: Number(order?.order.manualRefund?.refundAmount),
        currencyCode: order.order.currency,
      })
    );
    if (!response.isInternal) {
      startTravelRulePolling();
      openTravelRuleForm(response.sendPreviewId);
    } else {
      completeSend(response.sendPreviewId);
    }
    return response.sendPreviewId;
  }, [order, address, initiateExternalRefund]);

  const getTravelRuleStatus = useCallback(async () => {
    if (initiateRefundValue)
      return getTravelRuleInfoStatus(initiateRefundValue);
    return Promise.resolve({ status: false });
  }, [initiateRefundValue]);

  const checkTravelRuleSuccess = useCallback(
    (data: TravelRuleInfoStatus) => {
      if (data && data.status) {
        if (travelRuleWindow) {
          travelRuleWindow.close();
          setTravelRuleWindow(null);
        }
        if (initiateRefundValue) completeSend(initiateRefundValue);
        return true;
      }
      return null;
    },
    [travelRuleWindow, completeSend, initiateRefundValue]
  );

  const {
    start: startTravelRulePolling,
    error: travelRuleInfoError,
    isPolling: isPollingTravelRule,
  } = usePoll({
    poll: getTravelRuleStatus,
    onPoll: checkTravelRuleSuccess,
  });

  const isFormErrorResponse = (
    error: ErrorResponse
  ): error is FormErrorResponse => {
    return (error as FormErrorResponse).formErrors !== undefined;
  };

  useEffect(() => {
    if (completeRefundError || initiateRefundError || travelRuleInfoError) {
      if (initiateRefundError && isFormErrorResponse(initiateRefundError)) {
        setAddressError(
          initiateRefundError.formErrors.map((err) => err.reason).join('; ')
        );
      }
      setRefundResponseError(true);
    }
  }, [completeRefundError, initiateRefundError, travelRuleInfoError]);

  if (error) {
    return <ErrorPage error={error} tryAgainAction={() => navigate(0)} />;
  }

  return (
    <>
      {(initiateRefundError || completeRefundError || travelRuleInfoError) && (
        <Snackbar
          variant="danger"
          open={refundResponseError}
          onClose={() => setRefundResponseError(false)}
          message={'An error occurred, please try again'}
        />
      )}
      <div css={{ marginBottom: spacing.xs, textAlign: 'center' }}>
        <Typography variant="subtitle">
          <FormattedMessage id="page.refund.heading" />
        </Typography>
      </div>
      <Typography variant="bodySmall" color={palette.grey.default}>
        <FormattedMessage
          id="page.refund.subHeading"
          values={{
            amount: refundAmount,
          }}
        />
      </Typography>
      <div css={{ marginTop: spacing.xs, marginBottom: spacing.xxxs }}>
        <Input
          value={address}
          onChange={handleChange}
          placeholder={formatMessage({
            id: 'page.refund.refundAddress',
          })}
          endAdornment={
            <div css={{ cursor: 'pointer' }} onClick={handlePaste}>
              <Icon name="paste" />
            </div>
          }
        />
        {addressError && (
          <Typography variant="captionMedium" color={palette.danger.default}>
            {addressError}
          </Typography>
        )}
      </div>
      <Typography variant="captionSmallMedium" color={palette.grey.default}>
        <FormattedMessage id="page.refund.caption" />
      </Typography>
      {isPollingTravelRule && initiateRefundValue && (
        <Markdown
          markdown={`To comply with regulations, you need to provide information about the recipient. [Click here](${process.env.REACT_APP_WEB_APP_BASE_URL}/travel-rule-beneficiary-info/${initiateRefundValue}?isPayRefund=true) to go to the form.`}
          styles={{
            color: palette.grey.default,
            fontSize: '0.85rem',
          }}
        />
      )}
      <div css={{ marginTop: spacing.xs, marginBottom: spacing.xxs }}>
        <Button
          loading={
            initiateRefundLoading ||
            isPollingTravelRule ||
            completeRefundLoading
          }
          text={formatMessage({ id: 'page.refund.submit' })}
          onClick={refund}
          disabled={
            address.length === 0 ||
            initiateRefundLoading ||
            isPollingTravelRule ||
            completeRefundLoading
          }
        />
      </div>
      <Button
        variant="secondary"
        text={formatMessage({ id: 'page.refund.contactSupport' })}
        onClick={contactSupport}
      />
    </>
  );
};
